import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePallets, useWarehouses } from "@/hooks/useReferenceData";
import { dateInputStringToLocalStartOfDayMs, timestampToLocalDateInputString } from "@/lib/format";
import { useAppLocale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

interface CargoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cargo: any;
  sameProductCargoIds: string[];
}

type PalletOption = {
  value: string;
  label: string;
};

function getStatusOptions() {
  return [
    { value: "at_origin", label: m.status_at_origin() },
    { value: "upcoming", label: m.status_upcoming() },
    { value: "in_transit", label: m.status_in_transit() },
    { value: "in_spot", label: m.status_in_spot() },
    { value: "sold_out", label: m.status_sold_out() },
    { value: "delivered", label: m.status_delivered() },
  ];
}

function sanitizeContractPurchaseNumber(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 32);
}

function normalizeContractPurchaseNumber(value: string): string {
  return sanitizeContractPurchaseNumber(value.trim());
}

export function CargoEditDialog({ open, onOpenChange, cargo, sameProductCargoIds }: CargoEditDialogProps) {
  useAppLocale();
  const [form, setForm] = React.useState(toForm(cargo));
  const [applyPriceToSameProduct, setApplyPriceToSameProduct] = React.useState(false);
  const [applyDiffToSameProduct, setApplyDiffToSameProduct] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const bulkUpdate = useMutation(api.cargos.bulkUpdateCargos);
  const warehouses = useWarehouses();
  const pallets = usePallets();
  const statusOptions = getStatusOptions();
  const relatedCargoCount = sameProductCargoIds.length;
  const palletOptions = React.useMemo<PalletOption[]>(
    () => (pallets ?? []).map((pallet: any) => ({
      value: pallet._id,
      label: pallet.name,
    })),
    [pallets],
  );
  const selectedPalletLabels = React.useMemo(
    () => getSelectedPalletLabels(form.palletIds, palletOptions, cargo),
    [cargo, form.palletIds, palletOptions],
  );

  React.useEffect(() => {
    if (cargo && open) setForm(toForm(cargo));
  }, [cargo, open]);

  React.useEffect(() => {
    if (!open) {
      setApplyPriceToSameProduct(false);
      setApplyDiffToSameProduct(false);
    }
  }, [open]);

  const set = <K extends keyof ReturnType<typeof toForm>>(key: K, value: ReturnType<typeof toForm>[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (
      cargo?.product &&
      (typeof cargo.product.weight !== "number" ||
        !Number.isFinite(cargo.product.weight) ||
        cargo.product.weight <= 0)
    ) {
      toast.error("Product weight is required before saving this cargo.");
      return;
    }

    setSaving(true);
    try {
      const id = cargo._id ?? cargo.id;
      const pricePerPound = toOptionalNumber(form.pricePerPound);
      const diff = toOptionalNumber(form.diff);
      const updates: Array<{ id: any; fields: any }> = [{
        id,
        fields: {
          warehouseId: form.warehouseId || undefined,
          status: form.status || undefined,
          totalBags: toOptionalNumber(form.totalBags),
          availableBags: toOptionalNumber(form.availableBags),
          soldBags: toOptionalNumber(form.soldBags),
          warehouseBags: toOptionalNumber(form.warehouseBags),
          pricePerPound,
          diff,
          arrivalDate: dateInputStringToLocalStartOfDayMs(form.arrivalDate),
          departureDate: dateInputStringToLocalStartOfDayMs(form.departureDate),
          ociNumber: form.ociNumber || undefined,
          contractId: normalizeContractPurchaseNumber(form.contractId),
          palletIds: form.palletIds,
          featured: form.featured,
          archived: form.archived,
        },
      }];

      if ((applyPriceToSameProduct && pricePerPound !== undefined) || (applyDiffToSameProduct && diff !== undefined)) {
        for (const relatedCargoId of sameProductCargoIds) {
          updates.push({
            id: relatedCargoId,
            fields: {
              pricePerPound: applyPriceToSameProduct ? pricePerPound : undefined,
              diff: applyDiffToSameProduct ? diff : undefined,
            },
          });
        }
      }

      const result = await bulkUpdate({ updates: updates as any[] });
      const failedUpdate = Array.isArray(result)
        ? result.find((updateResult: any) => !updateResult.success)
        : null;

      if (failedUpdate) {
        throw new Error(failedUpdate.error || m.cargo_update_failed());
      }

      toast.success(
        ((applyPriceToSameProduct && pricePerPound !== undefined) || (applyDiffToSameProduct && diff !== undefined)) && relatedCargoCount > 0
          ? m.cargo_updated_related_fields({ count: relatedCargoCount })
          : m.cargo_updated(),
      );
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.cargo_update_failed());
    } finally {
      setSaving(false);
    }
  };

  const productName = cargo?.product?.name ?? cargo?.productId ?? "Unknown";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.cargo_edit()}</DialogTitle>
          <DialogDescription>
            {cargo?.ociNumber ? m.cargo_edit_description({ ociNumber: cargo.ociNumber, productName }) : `${m.product()}: ${productName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{m.warehouse()}</Label>
            <Select value={form.warehouseId} onValueChange={(v) => set("warehouseId", v)}>
              <SelectTrigger><SelectValue placeholder={m.cargo_select_warehouse()} /></SelectTrigger>
              <SelectContent>
                {warehouses?.map((w: any) => (
                  <SelectItem key={w._id} value={w._id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <PalletAssignmentsField
            options={palletOptions}
            selectedValues={form.palletIds}
            selectedLabels={selectedPalletLabels}
            loading={pallets === undefined}
            onChange={(nextValue) => set("palletIds", nextValue)}
          />

          <div className="grid gap-2">
            <Label>{m.status()}</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="cargo-featured">{m.status_featured()}</Label>
              <p className="text-xs text-muted-foreground">Feature this cargo on the public offer list.</p>
            </div>
            <Switch
              id="cargo-featured"
              checked={form.featured}
              onCheckedChange={(v) => set("featured", v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargos_total_bags()}</Label>
              <Input type="number" value={form.totalBags} onChange={(e) => set("totalBags", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_available_bags()}</Label>
              <Input type="number" value={form.availableBags} onChange={(e) => set("availableBags", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_sold_bags()}</Label>
              <Input type="number" value={form.soldBags} onChange={(e) => set("soldBags", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_warehouse_bags()}</Label>
              <Input type="number" value={form.warehouseBags} onChange={(e) => set("warehouseBags", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_price_per_lb_label()}</Label>
              <Input type="number" step="0.01" value={form.pricePerPound} onChange={(e) => set("pricePerPound", e.target.value)} />
              {relatedCargoCount > 0 && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {m.cargo_apply_price_to_related_help()}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant={applyPriceToSameProduct ? "default" : "outline"}
                    className="justify-start"
                    onClick={() => setApplyPriceToSameProduct((prev) => !prev)}
                  >
                    {applyPriceToSameProduct
                      ? m.cargo_apply_price_to_related_selected({ count: relatedCargoCount })
                      : m.cargo_apply_price_to_related({ count: relatedCargoCount })}
                  </Button>
                </>
              )}
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_diff_label()}</Label>
              <Input type="number" step="0.01" value={form.diff} onChange={(e) => set("diff", e.target.value)} />
              {relatedCargoCount > 0 && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {m.cargo_apply_diff_to_related_help()}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant={applyDiffToSameProduct ? "default" : "outline"}
                    className="justify-start"
                    onClick={() => setApplyDiffToSameProduct((prev) => !prev)}
                  >
                    {applyDiffToSameProduct
                      ? m.cargo_apply_diff_to_related_selected({ count: relatedCargoCount })
                      : m.cargo_apply_diff_to_related({ count: relatedCargoCount })}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_arrival_date()}</Label>
              <DatePicker value={form.arrivalDate} onChange={(v) => set("arrivalDate", v)} placeholder={m.select_date()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_departure_date()}</Label>
              <DatePicker value={form.departureDate} onChange={(v) => set("departureDate", v)} placeholder={m.select_date()} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_oci_number()}</Label>
              <Input value={form.ociNumber} onChange={(e) => set("ociNumber", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_contract_purchase_number()}</Label>
              <Input
                maxLength={32}
                value={form.contractId}
                onChange={(e) => set("contractId", sanitizeContractPurchaseNumber(e.target.value))}
                placeholder={m.cargo_contract_purchase_number_placeholder()}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>{m.cargo_bill_id()}</Label>
            <Input value={form.billId} disabled className="bg-muted" />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.archived}
              onCheckedChange={(v) => set("archived", !!v)}
            />
            <Label>{m.cargos_archived()}</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{m.cancel()}</Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? m.saving() : m.save_changes()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PalletAssignmentsField({
  options,
  selectedValues,
  selectedLabels,
  loading,
  onChange,
}: {
  options: PalletOption[];
  selectedValues: string[];
  selectedLabels: string[];
  loading: boolean;
  onChange: (nextValue: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, searchValue]);

  const toggleValue = React.useCallback(
    (value: string) => {
      onChange(
        selectedValues.includes(value)
          ? selectedValues.filter((selectedValue) => selectedValue !== value)
          : [...selectedValues, value],
      );
    },
    [onChange, selectedValues],
  );

  const triggerLabel = selectedLabels.length > 0
    ? selectedLabels.join(", ")
    : "-";

  return (
    <div className="grid gap-2">
      <Label>{m.cargos_pallet_location()}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-label={m.cargos_pallet_location()}
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="truncate text-left">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] max-w-[calc(100vw-3rem)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={m.cargos_search_pallet_location_placeholder()}
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              {loading ? (
                <CommandEmpty>Loading...</CommandEmpty>
              ) : filteredOptions.length === 0 ? (
                <CommandEmpty>No results found.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => toggleValue(option.value)}
                      >
                        <Check className={cn("mr-2 h-4 w-4", !isSelected && "text-transparent")} />
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedLabels.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {selectedLabels.map((label) => (
            <Badge key={label} variant="outline">{label}</Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function getSelectedPalletLabels(
  selectedPalletIds: string[],
  palletOptions: PalletOption[],
  cargo: any,
): string[] {
  if (selectedPalletIds.length === 0) {
    return [];
  }

  const labelById = new Map(palletOptions.map((option) => [option.value, option.label]));
  const optionLabels = selectedPalletIds
    .map((palletId) => labelById.get(palletId))
    .filter((label): label is string => Boolean(label));

  if (optionLabels.length > 0) {
    return optionLabels;
  }

  if (Array.isArray(cargo?.pallets)) {
    const cargoLabels = cargo.pallets
      .map((pallet: any) => String(pallet?.name ?? "").trim())
      .filter(Boolean);
    if (cargoLabels.length > 0) {
      return cargoLabels;
    }
  }

  const cargoPalletLocation = String(cargo?.palletLocation ?? "").trim();
  if (!cargoPalletLocation) {
    return [];
  }

  return cargoPalletLocation
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function toOptionalNumber(value: string) {
  if (value.trim() === "") return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toForm(c: any) {
  const toDateStr = (ts: any) => {
    if (ts == null || ts === "") return "";
    const n = typeof ts === "number" ? ts : Number(ts);
    return Number.isFinite(n) ? timestampToLocalDateInputString(n) : "";
  };
  return {
    warehouseId: c?.warehouseId ?? c?.warehouse?._id ?? "",
    palletIds: Array.isArray(c?.palletIds)
      ? c.palletIds.map((value: any) => String(value))
      : Array.isArray(c?.pallets)
        ? c.pallets
          .map((pallet: any) => pallet?._id ?? pallet?.id)
          .filter(Boolean)
          .map((value: any) => String(value))
        : [],
    status: c?.status ?? "at_origin",
    totalBags: c?.totalBags != null ? String(c.totalBags) : "",
    availableBags: c?.availableBags != null ? String(c.availableBags) : "",
    soldBags: c?.soldBags != null ? String(c.soldBags) : "",
    warehouseBags: c?.warehouseBags != null ? String(c.warehouseBags) : "",
    pricePerPound: c?.pricePerPound != null ? String(c.pricePerPound) : "",
    diff: c?.diff != null ? String(c.diff) : "",
    arrivalDate: toDateStr(c?.arrivalDate),
    departureDate: toDateStr(c?.departureDate),
    ociNumber: c?.ociNumber ?? "",
    contractId: c?.contractId ?? "",
    billId: c?.billId ?? "",
    featured: c?.featured ?? c?.product?.featured ?? false,
    archived: c?.archived ?? false,
  };
}
